var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Slidebar_1;
import React, { Component, createRef } from 'react';
import { KeyCode, Placement, ThemeTonalRange } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { DocumentContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, bool, element, eventListener, number, oneOf, oneOfType, string, } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { AbortableCloseEvent, CloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, isEnumValid, isFunction, isMobileResolution } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as CloseIcon } from '../../../resources/assets/components/DLS_Cross.svg';
import { GlobalActionsStackProvider } from '../../context/global-actions-stack-context.js';
import { GlobalActionsStackManager } from '../global-actions-stack-manager/global-actions-stack-manager.js';
import { SlidebarAPI } from './slidebar-api.js';
import './slidebar.css';
const SlidebarContainer = {
    name: '',
};
let Slidebar = Slidebar_1 = class Slidebar extends Component {
    constructor() {
        super(...arguments);
        this.touchClientStart = 0;
        this.slidebarRef = createRef();
        this.getSlidebarClassNames = () => {
            const { className, open, placement = Slidebar_1.defaultProps.placement, transparentOverlay, theme, } = this.props;
            const slidebarClasses = {
                'c-slidebar': true,
                'c-slidebar--transparent-overlay': Boolean(transparentOverlay),
                [`c-slidebar--${placement}`]: true,
                [`${className}`]: Boolean(className),
                [`t-content-${theme}`]: isEnumValid(ThemeTonalRange, theme),
                'is-open': Boolean(open),
            };
            return classNames(slidebarClasses);
        };
        this.shouldClose = () => {
            const { onBeforeClose } = this.props;
            let close = true;
            if (isFunction(onBeforeClose)) {
                const abort = () => close = false;
                onBeforeClose(new AbortableCloseEvent({ abort }));
            }
            return close;
        };
        this.closeSlidebar = () => {
            const shouldClose = this.shouldClose();
            if (!shouldClose) {
                return;
            }
            const { onClose } = this.props;
            if (isFunction(onClose)) {
                onClose(new CloseEvent());
            }
        };
        this.closeByDocument = (event) => {
            const classList = event.target.classList;
            const { closeByDocument = Slidebar_1.defaultProps.closeByDocument } = this.props;
            if (!closeByDocument || !classList.contains('c-slidebar')) {
                return;
            }
            this.closeSlidebar();
        };
        this.closeByEscape = (event) => {
            const { closeByEscape = Slidebar_1.defaultProps.closeByEscape } = this.props;
            if (!closeByEscape || event.keyCode !== KeyCode.ESC) {
                return;
            }
            this.closeSlidebar();
        };
        this.handleTouchEnd = (event) => {
            const { onClose, placement } = this.props;
            if (!isMobileResolution() || !isFunction(onClose)) {
                return;
            }
            // User navigation can be inconsistent at times. In order not to mix navigation events in
            // the list of items and the slide to close the slidebar, this second one needs to move a minimum
            // amount of pixels for the event to be triggered, preventing it from accidentally triggering.
            const minMoveToFireSlideRight = 80;
            const minMoveToFireSlideLeft = 140;
            const touchClientEnd = event.changedTouches[0].clientX;
            const shouldClose = placement === Placement.LEFT
                ? touchClientEnd < this.touchClientStart && this.touchClientStart - touchClientEnd > minMoveToFireSlideRight
                : touchClientEnd > this.touchClientStart && touchClientEnd - this.touchClientStart > minMoveToFireSlideLeft;
            if (shouldClose) {
                onClose(new CloseEvent());
            }
        };
        this.handleTouchStart = (event) => {
            const { onClose } = this.props;
            if (!isMobileResolution() || !isFunction(onClose)) {
                return;
            }
            this.touchClientStart = event.touches[0].clientX;
        };
    }
    componentDidMount() {
        const { document } = this.context;
        document?.documentElement.classList.add('u-prevent-scroll');
        this.slidebarRef.current?.focus();
        this.slidebarRef.current?.addEventListener('transitionend', (event) => {
            if (this.props.open && this.slidebarRef.current === event.target) {
                this.slidebarRef.current?.focus();
            }
        });
    }
    componentWillUnmount() {
        const { document } = this.context;
        document?.documentElement.classList.remove('u-prevent-scroll');
    }
    renderCloseSlidebar() {
        const { showCloseButton = Slidebar_1.defaultProps.showCloseButton } = this.props;
        if (!showCloseButton) {
            return null;
        }
        return (React.createElement("div", { className: "c-slidebar-close" },
            React.createElement(CloseIcon, { className: "c-slidebar-close__icon", onClick: this.closeSlidebar })));
    }
    render() {
        const { children, hideHeader, offset, title, uuid, } = this.props;
        return (React.createElement(GlobalActionsStackProvider, null,
            React.createElement("div", { className: this.getSlidebarClassNames(), onClick: this.closeByDocument, onKeyDown: this.closeByEscape, onTouchEnd: this.handleTouchEnd, onTouchStart: this.handleTouchStart, ref: this.slidebarRef, style: { border: `${offset}px solid transparent` }, tabIndex: 0 },
                React.createElement("div", { className: "c-slidebar__wrapper" },
                    hideHeader ? null : (React.createElement("div", { className: "c-slidebar-header" },
                        React.createElement("div", { className: "c-slidebar__title u-hide@empty" }, MessageService.translate(title ?? '')),
                        this.renderCloseSlidebar())),
                    React.createElement(Container, { className: "c-slidebar__content", uuid: uuid }, children),
                    React.createElement(GlobalActionsStackManager, { hideOnDesktop: false })))));
    }
};
Slidebar.contextType = DocumentContext;
Slidebar.propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    closeByDocument: bool,
    closeByEscape: bool,
    hideHeader: bool,
    offset: number,
    onBeforeClose: eventListener,
    onClose: eventListener,
    open: bool,
    placement: oneOf([Placement.LEFT, Placement.RIGHT]),
    showCloseButton: bool,
    theme: oneOf(['', ...enumValues(ThemeTonalRange)]),
    title: string,
    transparentOverlay: bool,
    uuid: string,
};
Slidebar.defaultProps = {
    closeByDocument: true,
    closeByEscape: true,
    offset: 0,
    placement: Placement.RIGHT,
    showCloseButton: true,
    sideHandler: false,
};
Slidebar = Slidebar_1 = __decorate([
    AwesomeComponent({ api: SlidebarAPI, containers: [SlidebarContainer], type: ComponentType.LAYOUT })
], Slidebar);
export { Slidebar, };
//# sourceMappingURL=slidebar.js.map